import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useState } from "react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";

/**
 * @typedef {Object} CommonPaginationProps
 * @property {number} currentPage
 * @property {number} totalPages
 * @property {number} totalRecords
 * @property {number} itemsPerPage
 * @property {(page: number) => void} onPageChange
 * @property {(itemsPerPage: number) => void} onItemsPerPageChange
 */
export const CommonPagination = ({
  currentPage,
  totalPages,
  totalRecords,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const [goToPage, setGoToPage] = useState("");

  // Calculate current range
  const startRecord = (currentPage - 1) * itemsPerPage + 1;
  const endRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  // Generate page numbers to display
  const generatePageNumbers = () => {
    const pages = [];
    const pairStart = Math.floor((currentPage - 1) / 2) * 2 + 1;
    const pairEnd = Math.min(pairStart + 1, totalPages);

    for (let i = pairStart; i <= pairEnd; i++) {
      pages.push(i);
    }

    // Show ellipsis and last page if not already in view
    if (pairEnd < totalPages - 1) {
      pages.push("ellipsis");
      pages.push(totalPages);
    } else if (pairEnd === totalPages - 1) {
      pages.push(totalPages); // no ellipsis if just one missing
    }

    return pages;
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (pageNum && pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum);
      setGoToPage("");
    }
  };

  const pageNumbers = generatePageNumbers();

  // Global Condition: Hide if total records are 10 or less
  if (totalRecords <= 10) {
    return null;
  }

  return (
    <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-4 p-4 md:p-6 bg-white/80 backdrop-blur-sm border border-gray-200/60 rounded-2xl shadow-sm transition-all">
      {/* Records info and items per page */}
      <div className="flex flex-col sm:flex-row items-center gap-4 text-sm text-gray-700">
        <span className="whitespace-nowrap font-medium">
          Showing {startRecord} - {endRecord} of{" "}
          {(totalRecords || 0).toLocaleString()}
        </span>

        <div className="hidden sm:flex items-center gap-2 whitespace-nowrap">
          <span className="text-gray-600">Records per page:</span>
          <Select
            value={(itemsPerPage || 10).toString()}
            onValueChange={(value) => onItemsPerPageChange(parseInt(value))}
          >
            <SelectTrigger className="w-20 h-9 text-xs border-gray-200 hover:border-primary/50 transition-colors">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="9">9</SelectItem>
              <SelectItem value="18">18</SelectItem>
              <SelectItem value="27">27</SelectItem>
              <SelectItem value="36">36</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Pagination controls */}
      <div className="flex flex-col sm:flex-row items-center gap-4">
        {/* Go to page */}
        <form
          onSubmit={handleGoToPage}
          className="hidden sm:flex items-center gap-2 text-sm"
        >
          <span className="whitespace-nowrap text-gray-600">Go to:</span>
          <Input
            type="number"
            min="1"
            max={totalPages}
            value={goToPage}
            onChange={(e) => setGoToPage(e.target.value)}
            className="w-14 h-9 text-xs border-gray-200 focus:border-primary"
            placeholder="#"
          />
          <Button
            type="submit"
            size="sm"
            variant="outline"
            className="h-9 px-3 text-xs hover:bg-primary hover:text-white hover:border-primary transition-all"
          >
            Go
          </Button>
        </form>

        {/* Page navigation */}
        <div className="flex items-center gap-1">
          {/* First page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="h-9 w-9 p-0 hover:bg-primary/10 hover:text-primary transition-all rounded-lg"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>

          {/* Previous page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="h-9 w-9 p-0 hover:bg-primary/10 hover:text-primary transition-all rounded-lg"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page numbers */}
          {pageNumbers.map((page, index) =>
            page === "ellipsis" ? (
              <span
                key={`ellipsis-${index}`}
                className="px-2 text-gray-400 text-xs"
              >
                ...
              </span>
            ) : (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "ghost"}
                size="sm"
                onClick={() => onPageChange(page)}
                className={`h-9 w-9 p-0 text-xs rounded-lg transition-all ${
                  currentPage === page
                    ? "bg-primary text-white shadow-md shadow-primary/30 hover:shadow-lg"
                    : "text-gray-700 hover:bg-primary/10 hover:text-primary"
                }`}
              >
                {page}
              </Button>
            )
          )}

          {/* Next page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="h-9 w-9 p-0 hover:bg-primary/10 hover:text-primary transition-all rounded-lg"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          {/* Last page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="h-9 w-9 p-0 hover:bg-primary/10 hover:text-primary transition-all rounded-lg"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
